/**
 * The starter library: what the home offers a user who has nothing yet. It
 * leads with the first run's five examples, the finished canvases its canned
 * use cases copy, named the way a run names them and in its tiles' order;
 * their ids differ per environment (the first run's `refs.ts`). The rest are D5 in the onboarding spec, five
 * canvases published by Cohesive Templates and mirrored into every
 * environment key for key, so one id resolves everywhere.
 */

import { config } from "@/config";
import { firstRunExamplesFor, type FirstRunExampleStory } from "@/onboarding";

export interface Template {
  id: string;
  title: string;
  /** One line on what the canvas is about, under the title. */
  tagline: string;
}

const EXAMPLES = firstRunExamplesFor(config.appEnv);

const EXAMPLE_COPY: ReadonlyArray<
  [FirstRunExampleStory, Omit<Template, "id">]
> = [
  [
    "rove",
    {
      title: "Weekly growth",
      tagline: "Turn your numbers into a dashboard and report",
    },
  ],
  [
    "tessel",
    {
      title: "Office days",
      tagline: "Research a question your team is debating",
    },
  ],
  [
    "supper",
    {
      title: "What customers told us",
      tagline: "Make sense of customer feedback",
    },
  ],
  [
    "brio",
    {
      title: "Onboarding research",
      tagline: "Turn a long document into a summary and slides",
    },
  ],
  [
    "kumo",
    {
      title: "Yuzu launch",
      tagline: "Explore creative directions for a campaign",
    },
  ],
];

/** The first run's examples, for this environment. */
export const EXAMPLE_TEMPLATES: readonly Template[] = EXAMPLE_COPY.map(
  ([story, copy]) => ({ id: EXAMPLES[story], ...copy }),
);

export const TEMPLATES: readonly Template[] = [
  ...EXAMPLE_TEMPLATES,
  {
    id: "4c60fa8a-2c9f-4214-b91a-251054208c24",
    title: "News Headlines Evolution",
    tagline: "How news headlines grew darker from 2008 to 2026",
  },
  {
    id: "9f79f319-97c2-4704-9c88-62f44a27756a",
    title: "F1 Data Canvas",
    tagline: "The 2026 Italian GP from the data: replay and pace",
  },
  {
    id: "4882547c-8868-47ab-9279-dee9f1405006",
    title: "10-inch Rack Retrofit",
    tagline: "Turning an IKEA cabinet into a 10-inch server rack",
  },
  {
    id: "a96a6de6-77eb-4459-97aa-a1753796b6ce",
    title: "Blood Moon",
    tagline: "The 28 August 2026 eclipse to scale, with timings",
  },
  {
    id: "483fe563-5d38-4220-963d-200bc5c29aaf",
    title: "Marathon Training",
    tagline: "Sixteen weeks to a 3:45 at CIM, pacing to fueling",
  },
];
